import React, { useEffect, useRef } from 'react';
import './ScanQueueChips.css';

export interface ScanQueueChip {
  readonly prefix: string;
  readonly sku?: string;
  /** When set, the chip reads `prefix - statusLabel - sku` on one line. */
  readonly statusLabel?: string;
}

export type ScanQueueLayout = 'grid' | 'stack';

export interface ScanQueueChipsProps {
  readonly items: readonly ScanQueueChip[];
  readonly onRemove: (prefix: string) => void;
  readonly emptyLabel?: string;
  readonly showCount?: boolean;
  /** `grid` is the two-column shelf list. `stack` is one full-width line per chip. */
  readonly layout?: ScanQueueLayout;
}

function statusTagClass(statusLabel: string): string {
  if (statusLabel === 'checked in') return 'scan-queue-status scan-queue-status-in';
  if (statusLabel === 'checked out') return 'scan-queue-status scan-queue-status-out';
  return 'scan-queue-status';
}

function formatChipLabel(item: ScanQueueChip): string {
  if (item.statusLabel) {
    return item.sku
      ? `${item.prefix} - ${item.statusLabel} - ${item.sku}`
      : `${item.prefix} - ${item.statusLabel}`;
  }
  return item.sku ? `${item.prefix} ${item.sku}` : item.prefix;
}

function ScanQueueChips({
  items,
  onRemove,
  emptyLabel = 'No scans yet',
  showCount = true,
  layout = 'grid',
}: ScanQueueChipsProps): React.ReactElement {
  const areaRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  useEffect(() => {
    const grew = items.length > prevCountRef.current;
    prevCountRef.current = items.length;
    if (!grew) return;
    const area = areaRef.current;
    if (!area) return;
    const frame = window.requestAnimationFrame(() => {
      area.scrollTo({ top: area.scrollHeight, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [items.length]);

  return (
    <>
      {showCount ? (
        <div className="scan-queue-count" aria-live="polite">
          {items.length} {items.length === 1 ? 'ID' : 'IDs'}
        </div>
      ) : null}
      <div
        className={layout === 'stack' ? 'scan-queue-area scan-queue-area-stack' : 'scan-queue-area'}
        ref={areaRef}
        aria-live="polite"
      >
        {items.length === 0 ? (
          <div className="scan-queue-empty">{emptyLabel}</div>
        ) : (
          <ul className={layout === 'stack' ? 'scan-queue scan-queue-stack' : 'scan-queue'}>
            {items.map((item) => (
              <li key={item.prefix} className="scan-queue-item" title={formatChipLabel(item)}>
                <span className="scan-queue-label">
                  <span className="scan-queue-id">{item.prefix}</span>
                  {item.statusLabel ? (
                    <>
                      {' - '}
                      <span className={statusTagClass(item.statusLabel)}>{item.statusLabel}</span>
                    </>
                  ) : null}
                  {item.sku ? (
                    <>
                      {item.statusLabel ? ' - ' : ' '}
                      <span className="scan-queue-sku">{item.sku}</span>
                    </>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="scan-queue-remove"
                  onClick={() => onRemove(item.prefix)}
                  aria-label={`Remove ${formatChipLabel(item)}`}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export default ScanQueueChips;
